import { FormikProps } from "formik"
import { forwardRef, Suspense, useRef, useState } from "react"
import { MERCHANT_ADMIN_PRICE_LISTS_ROUTE, MERCHANT_ADMIN_ROUTE } from "../../../../../constants/routes"
import { StandardLoadingButton } from "../../../../../shared/components/buttons/LoadingButton"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { CreateOrEditPriceListFormValues, PriceAdjustmentType } from "../priceListTypes"
import { LoadingSpinner } from "../../../../../shared/components/loading/LoadingSpinner"
import { QueryErrorBoundary } from "../../../../../core/api/components/QueryErrorBoundary"
import { CreateUpdatePriceListForm } from "../components/form/CreateUpdatePriceListForm"
import { useApiMutation } from "../../../../../core/api/useApiMutation"
import { SuccessDisplay } from "../../../../../core/api/errorTypes"
import {
    fetchDetailedPriceLists,
    FetchDetailedPriceListsResponse,
    PriceListQuery,
    updatePriceList,
} from "../priceListApi"
import { useNavigate } from "react-router"
import { useParams } from "react-router-dom"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { mapDataToCreateOrEdit } from "../helpers"

function transformData({ priceList }: FetchDetailedPriceListsResponse): CreateOrEditPriceListFormValues {
    const priceAdjust = priceList.priceSetup?.priceAdjustment
    const isAdjust = priceAdjust && (priceAdjust.type === PriceAdjustmentType.INCREASE || priceAdjust.type === PriceAdjustmentType.REDUCTION) && priceAdjust.percentage

    const percentage = isAdjust ?
        (priceAdjust.percentage.amount || 0) /
        Math.pow(10, priceAdjust.percentage.scale || 0) : ""

    return {
        name: priceList.name,
        customers: priceList.customers ?? [],
        products: (priceList.productGroups ?? []).flatMap((productGroup) => {
            return (productGroup.products ?? []).map((product) => ({
                ...product,
                productGroupId: productGroup.id,
            }))
        }),
        internalNote: priceList.internalNote,
        isActive: priceList.isActive,
        messageToCustomers: priceList.messageToCustomers,
        priceIncludesVat: priceList.priceIncludesVat ?? false,
        currencyCode: priceList.priceSetup?.currencyCode || "",
        vatCodeId: priceList.priceSetup?.vatCode?.id?.toString() || "",
        percentage: percentage.toString(),
        cardSubscriptions: priceList.cardSubscriptions ?? [],
        isDefaultPOSPriceList: priceList?.isDefaultPOSPriceList || false,
        priceAdjustmentType: priceList.priceSetup?.priceAdjustment.type || "",
        shops: (priceList.shops ?? []).map((shop) => shop.id),
    }
}

export function EditPriceList() {
    const formRef = useRef<FormikProps<CreateOrEditPriceListFormValues>>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)

    const handleSaveClick = async () => {
        setIsSubmitting(true)
        try {
            await formRef.current?.submitForm()
        } finally {
            setIsSubmitting(false)
        }
    }

    return (
        <>
            <LocalizedStrict id="edit-price-list-page-header" attrs={{ title: true }}>
                <PageHeader
                    page={Page.MERCHANT_PRICE_LIST}
                    title="Edit Price List"
                    rightElement={
                        <LocalizedStrict id="create-price-list-save-button">
                            <StandardLoadingButton loading={isSubmitting} onClick={handleSaveClick}>
                                Save
                            </StandardLoadingButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>

                        <LocalizedStrict id="merchant-nav-item-products-header">
                            <Breadcrumb>Products</Breadcrumb>
                        </LocalizedStrict>

                        <LocalizedStrict id="merchant-nav-item-price-lists">
                            <BreadcrumbLink to={MERCHANT_ADMIN_PRICE_LISTS_ROUTE}>Price Lists</BreadcrumbLink>
                        </LocalizedStrict>

                        <LocalizedStrict id="merchant-nav-item-price-list-edit">
                            <Breadcrumb>Edit</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <QueryErrorBoundary>
                <Suspense fallback={<LoadingSpinner />}>
                    <PriceList ref={formRef} />
                </Suspense>
            </QueryErrorBoundary>
        </>
    )
}

const PriceList = forwardRef<FormikProps<CreateOrEditPriceListFormValues>>((_, ref) => {
    const navigate = useNavigate()

    const { merchantId } = useMerchant()
    const { priceListId } = useParams()
    const priceListIdNumber = Number(priceListId)

    const detailedPriceList = useApiQuery(fetchDetailedPriceLists, {
        queryName: PriceListQuery.DETAILS,
        dependencies: {
            id: priceListIdNumber ?? 0,
        },
        suspense: true,
        useErrorBoundary: true,
        enabled: !!priceListIdNumber,
        select: transformData,
    })

    const { mutateAsync: callUpdatePriceList } = useApiMutation(updatePriceList, {
        successDisplay: SuccessDisplay.SNACKBAR,
        async onSuccess(_, __, context) {
            await context.queryClient.invalidateQueries({ queryKey: [PriceListQuery.LIST] })
            navigate(MERCHANT_ADMIN_PRICE_LISTS_ROUTE)
        },
    })

    const editPriceList = async (data: CreateOrEditPriceListFormValues) => {
        const mappedData = mapDataToCreateOrEdit(data, merchantId)
        await callUpdatePriceList({ priceListId: priceListIdNumber, priceList: mappedData })
    }

    return <CreateUpdatePriceListForm initialValues={detailedPriceList.data} ref={ref} onSubmit={editPriceList} />
})
